import React from "react";
import { FiMoreVertical } from "react-icons/fi";
import { Menu, MenuItem } from "@mui/material";

import {
  TableContainer,
  StyledTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  ActionButton,
} from "./styles";

export interface Column<T> {
  key: string;
  header: string;
  headerRender?: () => React.ReactNode;
  onHeaderClick?: () => void;
  render?: (row: T) => React.ReactNode;
  width?: string;
}

export interface TableAction<T> {
  label: string | ((row: T) => string);
  icon?: React.ReactNode | ((row: T) => React.ReactNode);
  color?: string | ((row: T) => string);
  onClick: (row: T, event?: React.MouseEvent) => void;
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  position?: "menu" | "outside";
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  actions?: TableAction<T>[];
  rowClassName?: (row: T) => string;
}

export function Table<T>({ columns, data, onRowClick, actions, rowClassName }: TableProps<T>) {
  return (
    <TableContainer>
      <StyledTable>
        <Thead>
          <Tr>
            {columns.map((col) => (
              <Th
                key={col.key}
                colWidth={col.width}
                $clickable={!!col.onHeaderClick}
                onClick={col.onHeaderClick}
              >
                {col.headerRender ? col.headerRender() : col.header}
              </Th>
            ))}
            {actions && actions.length > 0 && <Th className="actions-header"></Th>}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, index) => (
            <Tr
              key={index}
              className={rowClassName ? rowClassName(row) : ""}
              clickable={!!onRowClick}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((col) => (
                <Td key={col.key} data-label={col.header} colWidth={col.width}>
                  {col.render ? col.render(row) : (row as any)[col.key]}
                </Td>
              ))}
              {actions && actions.length > 0 && (
                <Td className="actions-cell">
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
                    {actions
                      .filter(a => a.position === "outside" && !(a.hidden && a.hidden(row)))
                      .map((action, idx) => {
                        const isDisabled = action.disabled && action.disabled(row);
                        const label = typeof action.label === "function" ? action.label(row) : action.label;
                        const icon = typeof action.icon === "function" ? action.icon(row) : action.icon;
                        return (
                          <ActionButton
                            key={`outside-${idx}`}
                            disabled={isDisabled}
                            style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? "not-allowed" : "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) action.onClick(row, e);
                            }}
                            title={label}
                          >
                            {icon}
                          </ActionButton>
                        );
                      })}
                    {actions.filter(a => a.position !== "outside" && !(a.hidden && a.hidden(row))).length > 0 && (
                      <ActionsMenu row={row} actions={actions.filter(a => a.position !== "outside" && !(a.hidden && a.hidden(row)))} />
                    )}
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </StyledTable>
    </TableContainer>
  );
}

function ActionsMenu<T>({ row, actions }: { row: T; actions: TableAction<T>[] }) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <ActionButton isOpen={open} onClick={handleClick}>
        <FiMoreVertical size={20} />
      </ActionButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
            minWidth: "210px",
            width: "max-content",
            padding: "4px 0",
          }
        }}
      >
        {actions.map((action, idx) => {
          if (action.hidden && action.hidden(row)) return null;

          const isDisabled = action.disabled && action.disabled(row);
          const itemLabel = typeof action.label === "function" ? action.label(row) : action.label;
          const itemIcon = typeof action.icon === "function" ? action.icon(row) : action.icon;
          const itemColor =
            (typeof action.color === "function" ? action.color(row) : action.color) ||
            (React.isValidElement(itemIcon) && (itemIcon.props as any)?.color) ||
            "var(--primary)";

          return (
            <MenuItem
              key={idx}
              disabled={isDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (isDisabled) return;
                setAnchorEl(null);
                action.onClick(row, e);
              }}
              sx={{
                width: "100%",
                height: "40px",
                padding: "4px 16px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? "not-allowed" : "pointer",
                backgroundColor: "var(--white)",
                "&:hover": {
                  backgroundColor: isDisabled ? "var(--white)" : "#f5f5f5",
                },
                svg: {
                  width: "18px",
                  height: "18px",
                  color: itemColor,
                  flexShrink: 0,
                },
                span: {
                  fontSize: "15px",
                  fontWeight: 500,
                  color: itemColor,
                  whiteSpace: "nowrap",
                }
              }}
            >
              {itemIcon}
              <span>{itemLabel}</span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
